import { KeyboardEvent, useLayoutEffect, useRef } from 'react';
import { cn } from '@utils/cn';
import { ITEM_HEIGHT, SETTLE_DELAY, VISIBLE_ITEMS } from './constants';
import { WheelColumnProps } from './types';

const PADDING = ((VISIBLE_ITEMS - 1) / 2) * ITEM_HEIGHT;

export const WheelColumn = <T extends string | number>({
  label,
  options,
  value,
  onChange,
  className,
}: WheelColumnProps<T>) => {
  const ref = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const scrolling = useRef(false);
  const index = Math.max(
    0,
    options.findIndex((el) => el.value === value),
  );

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || scrolling.current) return;
    el.scrollTop = index * ITEM_HEIGHT;
  }, [index]);

  useLayoutEffect(() => () => clearTimeout(timer.current), []);

  const settle = () => {
    const el = ref.current;
    if (!el) return;
    scrolling.current = false;
    const next = Math.min(options.length - 1, Math.max(0, Math.round(el.scrollTop / ITEM_HEIGHT)));
    if (options[next] && options[next].value !== value) onChange(options[next].value);
    else el.scrollTop = next * ITEM_HEIGHT;
  };

  const handleScroll = () => {
    scrolling.current = true;
    clearTimeout(timer.current);
    timer.current = setTimeout(settle, SETTLE_DELAY);
  };

  const select = (next: number) => {
    const option = options[next];
    if (!option) return;
    ref.current?.scrollTo({ top: next * ITEM_HEIGHT, behavior: 'smooth' });
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    const next = index + (e.key === 'ArrowDown' ? 1 : -1);
    const option = options[next];
    if (option) onChange(option.value);
  };

  return (
    <div
      ref={ref}
      role="listbox"
      tabIndex={0}
      aria-label={label}
      aria-activedescendant={`${label}-${index}`}
      onScroll={handleScroll}
      onKeyDown={handleKeyDown}
      style={{ height: ITEM_HEIGHT * VISIBLE_ITEMS, paddingBlock: PADDING }}
      className={cn(
        'relative z-10 snap-y snap-mandatory overflow-y-scroll overscroll-contain outline-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}>
      {options.map((option, i) => (
        <div
          key={option.value}
          id={`${label}-${i}`}
          role="option"
          aria-selected={i === index}
          onClick={() => select(i)}
          style={{ height: ITEM_HEIGHT }}
          className={cn(
            'flex snap-center items-center justify-center text-xl tabular-nums text-neutral-400 transition-colors',
            i === index && 'font-semibold text-neutral-800',
          )}>
          {option.label}
        </div>
      ))}
    </div>
  );
};
